import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Divider,
    Grid,
    Chip,
    CircularProgress,
    Alert,
} from "@mui/material";
import { Close, Inventory2Outlined } from "@mui/icons-material";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";

const fmtMoney = (n) => {
    if (n === null || n === undefined || n === "") return "—";
    const num = Number(n);
    if (Number.isNaN(num)) return String(n);
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
    }).format(num);
};

const fmtPivotDate = (d) => {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleString("en-IN", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "—";
    }
};

const Row = ({ label, value }) => (
    <Grid item xs={12} sm={6}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
            {value ?? "—"}
        </Typography>
    </Grid>
);

/** Modal: product or service details. Loads the latest row when opened; `initialProduct` fills in until then. */
const CRMProductServiceDetailDialog = ({ open, onClose, productId, initialProduct = null }) => {
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    const effective = useMemo(() => row || initialProduct, [row, initialProduct]);

    useEffect(() => {
        if (!open) {
            setRow(null);
            setFetchError(null);
            return;
        }
        if (!productId || Number.isNaN(Number(productId))) {
            setRow(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setFetchError(null);
        (async () => {
            try {
                const res = await axiosInstance.get(`${base_url}/crm/products-services/${productId}`);
                const data = res.data?.data ?? res.data;
                if (!cancelled) setRow(data && typeof data === "object" ? data : null);
            } catch (err) {
                if (!cancelled) {
                    setFetchError(err?.response?.data?.message || "Could not load the latest details.");
                    setRow(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, productId]);

    const display = effective;
    const name = display?.name || "Product / service";
    const type = display?.type;
    const createdBy = display?.createdBy ?? display?.created_by_user;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            backgroundColor: "rgba(17,24,39,0.06)",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                        }}
                    >
                        <Inventory2Outlined sx={{ fontSize: 18, color: "text.primary" }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1.2 }} noWrap title={name}>
                            {name}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.75, mt: 0.5, flexWrap: "wrap" }}>
                            <Chip
                                size="small"
                                label={type === "service" ? "Service" : "Product"}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                    height: 22,
                                    backgroundColor: type === "service" ? "#eff6ff" : "#f0fdf4",
                                    color: type === "service" ? "#1d4ed8" : "#166534",
                                }}
                            />
                            {display?.id != null ? (
                                <Typography variant="caption" color="text.secondary">
                                    ID {display.id}
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>
                </Box>
                <IconButton size="small" onClick={onClose} aria-label="Close">
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2.5 }}>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {!loading && fetchError && !display && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        {fetchError}
                    </Alert>
                )}
                {display && (
                    <Grid container spacing={2}>
                        <Row label="Price" value={display.price != null && display.price !== "" ? fmtMoney(display.price) : "—"} />
                        <Row label="SKU" value={display.sku} />
                        <Row label="Category" value={display.category} />
                        <Row
                            label="Status"
                            value={
                                display.status ? (
                                    <Chip
                                        size="small"
                                        label={display.status === "inactive" ? "Inactive" : "Active"}
                                        sx={{
                                            height: 22,
                                            fontSize: "0.7rem",
                                            fontWeight: 600,
                                            backgroundColor: display.status === "inactive" ? "#fef2f2" : "#ecfdf5",
                                            color: display.status === "inactive" ? "#991b1b" : "#047857",
                                        }}
                                    />
                                ) : (
                                    "—"
                                )
                            }
                        />
                        <Row
                            label="Tax %"
                            value={display.tax_percent != null && display.tax_percent !== "" ? String(display.tax_percent) : "—"}
                        />
                        <Row label="Discount" value={display.discount != null && display.discount !== "" ? String(display.discount) : "—"} />
                        {type !== "service" ? (
                            <Row
                                label="Stock qty"
                                value={display.stock_quantity != null && display.stock_quantity !== "" ? String(display.stock_quantity) : "—"}
                            />
                        ) : (
                            <Row label="Duration" value={display.duration != null && display.duration !== "" ? String(display.duration) : "—"} />
                        )}
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600 }}>
                                Description
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                                {display.description?.trim() ? display.description : "—"}
                            </Typography>
                        </Grid>
                        {createdBy?.name || createdBy?.email ? (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Catalog owner
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.25 }}>
                                    {[createdBy.name, createdBy.email].filter(Boolean).join(" · ") || "—"}
                                </Typography>
                            </Grid>
                        ) : null}
                        {display.pivot && (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        mt: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: `1px solid ${WB_UI.color.border}`,
                                        backgroundColor: "rgba(0,0,0,0.02)",
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                                        Link on this deal
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.75, fontSize: "0.8rem" }}>
                                        Linked {fmtPivotDate(display.pivot.created_at)}
                                        {display.pivot.updated_at &&
                                        display.pivot.updated_at !== display.pivot.created_at
                                            ? ` · updated ${fmtPivotDate(display.pivot.updated_at)}`
                                            : ""}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button size="small" onClick={onClose} sx={WB_SX.secondaryButton}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CRMProductServiceDetailDialog;
